import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Check, Star, Gift } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { api, errMsg } from '@/lib/api';
import { useState } from 'react';

function planDisplayPrice(plan) {
  if (plan.price_usd === 0) return { primary: '$0', sub: 'Forever free' };
  if (plan.price_usd === null || plan.price_usd === undefined) return { primary: 'Custom', sub: 'Talk to sales' };
  return { primary: `$${plan.price_usd}`, sub: `per ${plan.period}` };
}

export default function PricingGrid({ plans, compact = false, onEnterprise, currentPlanKey }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);

  const startCheckout = async (plan) => {
    if (plan.key === currentPlanKey) return;
    if (plan.key === 'free') {
      if (!user) return navigate('/register');
      return navigate('/app/dashboard');
    }
    if (!user) {
      // Persist intent so post-signup we can auto-checkout
      try { localStorage.setItem('ageorush_pending_plan', plan.key); } catch (_e) { /* noop */ }
      return navigate('/register');
    }
    setLoadingKey(plan.key);
    try {
      const r = await api.post('/billing/checkout', { plan: plan.key });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      // 402 (plan gate) already shows a rich Upgrade toast via the interceptor.
      if (err?.response?.status !== 402) {
        toast.error(errMsg(err, 'Checkout failed'));
      }
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className={`grid gap-4 ${compact ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
      {plans.map((plan) => {
        const price = planDisplayPrice(plan);
        const isPopular = plan.popular;
        const isCurrent = currentPlanKey && plan.key === currentPlanKey;
        const trialDays = Number(plan.trial_days || 0);
        const hasTrial = plan.key !== 'free' && trialDays > 0 && plan.available !== false;
        const ctaLabel = isCurrent
          ? 'Current plan'
          : loadingKey === plan.key
            ? 'Redirecting…'
            : hasTrial
              ? `Start ${trialDays}-day free trial`
              : plan.cta;
        return (
          <Card
            key={plan.key}
            data-testid={`pricing-card-${plan.key}`}
            className={`border card-elevated ${isPopular && !isCurrent ? 'border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/40 relative' : ''} ${isCurrent ? 'border-[hsl(var(--accent))] relative' : ''} flex flex-col p-6`}
          >
            {isCurrent ? (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gap-1 bg-secondary text-secondary-foreground border">
                Current plan
              </Badge>
            ) : isPopular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gap-1 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <Star className="h-3 w-3" /> Most popular
              </Badge>
            )}
            <div className="flex items-baseline justify-between">
              <div className="font-display text-lg font-semibold">{plan.name}</div>
            </div>
            <div className="mt-3">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-4xl font-semibold tabular tracking-tight">{price.primary}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {hasTrial ? `Then ${price.primary}/${plan.period}` : price.sub}
              </div>
              {hasTrial && (
                <Badge
                  variant="outline"
                  className="mt-2 gap-1 border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[11px] font-medium"
                  data-testid={`pricing-trial-badge-${plan.key}`}
                >
                  <Gift className="h-3 w-3" /> {trialDays}-day free trial
                </Badge>
              )}
            </div>
            <p className="mt-3 text-sm text-muted-foreground min-h-[40px]">{plan.tagline}</p>
            <ul className="mt-4 space-y-2 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Button
                className="w-full"
                variant={isCurrent ? 'outline' : isPopular ? 'default' : plan.key === 'free' ? 'outline' : 'default'}
                onClick={() => startCheckout(plan)}
                disabled={isCurrent || loadingKey === plan.key || (plan.key !== 'free' && plan.available === false)}
                data-testid={`pricing-cta-${plan.key}`}
              >
                {ctaLabel}
              </Button>
              {hasTrial && !isCurrent && (
                <div className="text-[11px] text-muted-foreground text-center mt-2" data-testid={`pricing-trial-note-${plan.key}`}>
                  No charge for {trialDays} days · Cancel anytime
                </div>
              )}
              {plan.key !== 'free' && plan.available === false && (
                <div className="text-[11px] text-muted-foreground text-center mt-2">Billing activation in progress</div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
